/**
 * Utility functions for fidelity form data transformations
 */

import { isValidName, isEmailValid } from '../../../../../utils/Validator';

// ========== NAME CONVERSIONS ==========

export const parseFullName = (fullName) => {
  const parts = fullName?.trim().split(' ') || [];
  return {
    lastName: parts[0] || '',
    firstName: parts.slice(1).join(' ') || '',
  };
};

// ========== DATA TRANSFORMATION FOR API ==========

export const excludeUserOwnedData = (formData, user) => {
  const result = { ...formData };

  if (user?.email) {
    delete result.email;
  } else {
    result.email = formData.email || '';
  }

  if (user?.phoneNumber) {
    delete result.phoneNumber;
  } else {
    result.phoneNumber = formData.telephone || formData.phoneNumber || '';
  }

  if (user?.nom) {
    delete result.nom;
  }

  if (user?.prenom) {
    delete result.prenom;
  }

  if (user?.birthDate) {
    delete result.birthDate;
  }

  if (user?.gender) {
    delete result.gender;
  }

  return result;
};


/**
 * Get fidelity form data for a specific company
 * @param {Object} user - User object
 * @param {string} companyId - Company ID to search for
 * @returns {Object|null} Fidelity form data if exists, otherwise null
 */
export const getFidelityFormData = (user, companyId) => {
  if (!user?.fidelityFormsByCompany || !Array.isArray(user.fidelityFormsByCompany)) {
    return null;
  }
  
  return user.fidelityFormsByCompany.find(form => form.companyId === companyId) || null;
};


// ========== USER DISPLAY HELPERS ==========

export const getUserDisplayName = (userData) => {
  if (!userData?.prenom || !userData?.nom) return '';
  return `${userData.prenom.toUpperCase().slice(0, 1)}. ${userData.nom}`;
};

/**
 * Get user data with priority to fidelity form data for a specific company
 * @param {Object} user - User object
 * @param {string} companyId - Company ID to search for
 * @returns {Object} Fidelity form data if exists, otherwise user data
 */
export const getUserDataWithFidelityPriority = (user, companyId) => {
  if (!companyId) return user;
  const fidelityFormData = getFidelityFormData(user, companyId);
  return fidelityFormData || user;
};

// ========== VALIDATION HELPERS ==========

// Form field percentage weight constants for progress calculation
const FIELD_PERCENTAGE_WEIGHT = {
  NAME: 15,
  FIRST_NAME: 15,
  EMAIL: 15,
  GENDER: 10,
  BIRTH_DATE: 15,
  PHONE: 15,
  FAVORITE_STORE: 15,
};

/**
 * Calculate form completion progress percentage
 * @param {Object} formData - Form data object
 * @param {boolean} isEmailValidProp - Email validation status
 * @param {boolean} isValidBirthDate - Birth date validation status
 * @param {boolean} isValidPhoneNumber - Phone number validation status
 * @param {boolean} isValidGenderProp - Gender validation status (default: true)
 * @returns {number} Completion percentage (0-100)
 */
export const calculateFormProgress = (formData, isEmailValidProp, isValidBirthDate, isValidPhoneNumber, isValidGenderProp = true) => {
  let completed = 0;
  
  if (formData.coordonnees?.nom && isValidName(formData.coordonnees.nom)) {
    completed += FIELD_PERCENTAGE_WEIGHT.NAME;
  }
  if (formData.coordonnees?.prenom && isValidName(formData.coordonnees.prenom)) {
    completed += FIELD_PERCENTAGE_WEIGHT.FIRST_NAME;
  }
  if (formData.coordonnees?.email?.length > 0 && isEmailValidProp) {
    completed += FIELD_PERCENTAGE_WEIGHT.EMAIL;
  }
  if (formData.genre && isValidGenderProp) {
    completed += FIELD_PERCENTAGE_WEIGHT.GENDER;
  }
  if (formData.dateNaissance && isValidBirthDate) {
    completed += FIELD_PERCENTAGE_WEIGHT.BIRTH_DATE;
  }
  if (formData.telephone?.length > 0 && isValidPhoneNumber) {
    completed += FIELD_PERCENTAGE_WEIGHT.PHONE;
  }
  if (formData.favoriteStore) {
    completed += FIELD_PERCENTAGE_WEIGHT.FAVORITE_STORE;
  }
  
  return Math.min(100, completed);
};
